// src/components/gestionEvento/CategoriasRow.js

import React, { useState, useEffect, useMemo } from 'react'; // Añadir useMemo
import {
  TableRow,
  TableCell,
  IconButton,
  TextField,
  Tooltip,
  Checkbox,
  CircularProgress,
  Box, // Para posible layout dentro de celda
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete'; // Renombrar a DeleteIcon si prefieres
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { alpha } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'; // <-- Importar icono de arrastre
import PropTypes from 'prop-types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Quitar la función de aquí, se recibirá como prop
const calcularRangoFechasNacimiento = (fechaCorte, edadMin, edadMax) => {
  const corteDate = fechaCorte ? new Date(fechaCorte + 'T00:00:00') : null;
  if (!corteDate || isNaN(corteDate.getTime())) return 'Fecha Inválida';
  const anioCorte = corteDate.getFullYear();
  const numEdadMin = Number(edadMin);
  const numEdadMax = Number(edadMax);
  if (isNaN(numEdadMin) || isNaN(numEdadMax) || numEdadMin < 0 || numEdadMax < 0) return 'Edad Inválida';
  if (numEdadMin > numEdadMax) return 'Edades Invertidas';
  const fechaNacimientoMaxDate = new Date(anioCorte - numEdadMin, 11, 31);
  const fechaNacimientoMinDate = new Date(anioCorte - numEdadMax, 0, 1);
  const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
  try {
      const minStr = fechaNacimientoMinDate.toLocaleDateString('es-ES', options);
      const maxStr = fechaNacimientoMaxDate.toLocaleDateString('es-ES', options);
      return `${minStr} - ${maxStr}`;
  } catch (e) { return "Error Formato"; }
};
const CategoriasRow = ({
  id,
  competenciaId,
  categoria,
  onUpdateCategoria,
  // onDeleteCategoria, // Ya no se usa esta prop
  isSelected,
  handleClick,
  setSnackbar,
  onRequestDeleteSingle, // Recibimos la función correcta
  onRowDirtyChange,
  bulkMode = false,
  selectionLocked = false,
  rowIndex = 0,  
}) => {

  const {
    attributes,
    listeners, // <-- Estos son los listeners para el drag handle
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id }); // El ID de la categoría se usa para identificar el elemento arrastrable

    const stripe = (theme) => {
    const bands = [
      alpha(theme.palette.primary.main, 0.05),
      alpha(theme.palette.success.main, 0.05),
      alpha(theme.palette.warning.main, 0.06),
      alpha(theme.palette.info.main,    0.06),
    ];
    return bands[rowIndex % bands.length];
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.9 : 1,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
    backgroundColor: stripe, // 👈 aplica color por fila
  };

  // --- Estados para edición ---
  const [editFormData, setEditFormData] = useState({
    codigo: categoria?.codigo || '',
    nombre: categoria?.nombre || '',
    edadMin: categoria?.edadMin ?? '', // Usar ?? para manejar 0 correctamente
    edadMax: categoria?.edadMax ?? '',
    // Usar fecha de corte de la categoría, o la default si no existe
    fechaCorte: categoria?.fechaCorte || '',
  });

  const [originalData, setOriginalData] = useState({ ...editFormData });
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Estado para feedback de guardado

  // --- Validaciones y Cálculo ---
  // Memoizar el cálculo del rango de fechas
  const rangoFechasNacimiento = useMemo(() => {
    const fechaValida = editFormData.fechaCorte && !isNaN(new Date(editFormData.fechaCorte + 'T00:00:00').getTime());
    const edadMinValida = editFormData.edadMin !== '' && !isNaN(Number(editFormData.edadMin));
    const edadMaxValida = editFormData.edadMax !== '' && !isNaN(Number(editFormData.edadMax));

    if (fechaValida && edadMinValida && edadMaxValida) {
      // LLAMADA DIRECTA a la función definida arriba o importada
      return calcularRangoFechasNacimiento(
        editFormData.fechaCorte,
        editFormData.edadMin,
        editFormData.edadMax
      );
    }
    return 'Datos incompletos';
  }, [editFormData.fechaCorte, editFormData.edadMin, editFormData.edadMax]); // Dependencia de la función ya no es necesaria

    const buildValidation = (data) => {
    const edadMinNum = Number(data.edadMin);
    const edadMaxNum = Number(data.edadMax);
    const errors = [];
    if (!data.codigo?.trim()) errors.push("Código es requerido.");
    if (!data.nombre?.trim()) errors.push("Nombre es requerido.");
    if (data.edadMin === '' || isNaN(edadMinNum)) errors.push("Edad Mín. inválida.");
    if (data.edadMax === '' || isNaN(edadMaxNum)) errors.push("Edad Máx. inválida.");
    if (!isNaN(edadMinNum) && !isNaN(edadMaxNum) && edadMinNum > edadMaxNum) errors.push("Edad Mín. no puede ser mayor a Edad Máx.");
    if (!data.fechaCorte) errors.push("Fecha de Corte es requerida.");
    else if (isNaN(new Date(data.fechaCorte + 'T00:00:00').getTime())) errors.push("Formato de Fecha de Corte inválido.");
    const isValid = errors.length === 0;
    const payload = {
      codigo: String(data.codigo || '').trim(),
      nombre: String(data.nombre || '').trim(),
      edadMin: Number(data.edadMin),
      edadMax: Number(data.edadMax),
      fechaCorte: data.fechaCorte,
    };
    return { isValid, errors, payload };
  };

  const editableCellSx = {
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiInputBase-root': {
    backgroundColor: (t) => alpha(t.palette.primary.main, 0.06), // 👈 color suave
    borderRadius: 4,
    transition: 'background-color .2s',
  },
};

  // Notificar al padre cuando la fila queda “sucia/limpia”
  useEffect(() => {
    if (!onRowDirtyChange) return;
    const { isValid, errors, payload } = buildValidation(editFormData);
    onRowDirtyChange(id, {
      hasChanges,
      isValid,
      errors,
      payload,
      original: originalData,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges, editFormData]); // reporta en cambios

  // Actualizar form y original si la categoría cambia desde las props
  useEffect(() => {
    const newFormData = {
      codigo: categoria?.codigo || '',
      nombre: categoria?.nombre || '',
      edadMin: categoria?.edadMin ?? '',
      edadMax: categoria?.edadMax ?? '',
      fechaCorte: categoria?.fechaCorte || '',
    };
    // Solo actualizar si los datos realmente cambiaron para evitar loops
    if (JSON.stringify(newFormData) !== JSON.stringify(editFormData)) {
      setEditFormData(newFormData);
      setOriginalData(newFormData);
      setHasChanges(false);
  }
}, [categoria]); // Quitar fechaCorteDefault de dependencias


  // Detectar cambios
  useEffect(() => {
    const isDifferent =
      // Comparar strings y números correctamente
      String(editFormData.codigo).trim() !== String(originalData.codigo).trim() ||
      String(editFormData.nombre).trim() !== String(originalData.nombre).trim() ||
      Number(editFormData.edadMin) !== Number(originalData.edadMin) ||
      Number(editFormData.edadMax) !== Number(originalData.edadMax) ||
      editFormData.fechaCorte !== originalData.fechaCorte;
    setHasChanges(isDifferent);
  }, [editFormData, originalData]);

  // Manejar cambios en los campos
  // ... (handleFieldChange - sin cambios) ...
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if ((name === 'edadMin' || name === 'edadMax') && Number(value) < 0) return;
    setEditFormData(prev => ({ ...prev, [name]: value, }));
  };

// Guardar cambios (en fila)
const handleSaveClick = async (event) => {
  event.stopPropagation(); // Evitar que el click se propague a la fila

  // Si estás usando modo bulk, no permitas guardar individual (por seguridad)
  if (bulkMode) {
    setSnackbar?.({
      open: true,
      message: 'Hay varias filas en edición. Usa "Guardar todas".',
      severity: 'info',
    });
    return;
  }

  setIsSaving(true);

  // --- Normalizador + validación ---
  const edadMinNum = Number(editFormData.edadMin);
  const edadMaxNum = Number(editFormData.edadMax);

  const errors = [];
  if (!String(editFormData.codigo || '').trim()) errors.push('Código es requerido.');
  if (!String(editFormData.nombre || '').trim()) errors.push('Nombre es requerido.');
  if (editFormData.edadMin === '' || isNaN(edadMinNum)) errors.push('Edad Mín. inválida.');
  if (editFormData.edadMax === '' || isNaN(edadMaxNum)) errors.push('Edad Máx. inválida.');
  if (!isNaN(edadMinNum) && !isNaN(edadMaxNum) && edadMinNum > edadMaxNum)
    errors.push('Edad Mín. no puede ser mayor a Edad Máx.');
  if (!editFormData.fechaCorte) errors.push('Fecha de Corte es requerida.');
  else if (isNaN(new Date(editFormData.fechaCorte + 'T00:00:00').getTime()))
    errors.push('Formato de Fecha de Corte inválido.');

  if (errors.length > 0) {
    setSnackbar?.({ open: true, message: `Errores: ${errors.join(' ')}`, severity: 'error' });
    setIsSaving(false);
    return;
  }

  const payload = {
    codigo: String(editFormData.codigo || '').trim(),
    nombre: String(editFormData.nombre || '').trim(),
    edadMin: edadMinNum,
    edadMax: edadMaxNum,
    fechaCorte: editFormData.fechaCorte,
    // NO enviar catNumber aquí (lo maneja DnD)
  };

  try {
    const result = await onUpdateCategoria(competenciaId, id, payload);

    if (result?.wasUpdated) {
      // Sin recargar toda la tabla 👇
      setOriginalData({ ...editFormData });
      setHasChanges(false);

      // Notificar al padre que esta fila quedó limpia
      onRowDirtyChange?.(id, {
        hasChanges: false,
        isValid: true,
        errors: [],
        payload,
        original: { ...editFormData },
      });

      setSnackbar?.({ open: true, message: 'Categoría actualizada.', severity: 'success' });
    } else {
      setSnackbar?.({
        open: true,
        message: `Error al actualizar: ${result?.reason || 'Intente de nuevo'}`,
        severity: 'warning',
      });
      // Opcional: revertir a original si falló
      setEditFormData({ ...originalData });
      setHasChanges(false);
    }
  } catch (error) {
    setSnackbar?.({ open: true, message: `Error: ${error.message}`, severity: 'error' });
    // Revertir al último estado persistido
    setEditFormData({ ...originalData });
    setHasChanges(false);
  } finally {
    setIsSaving(false);
  }
};


  // Cancelar cambios
const handleCancelClick = (event) => {
  event.stopPropagation();
  setEditFormData({ ...originalData });
  setHasChanges(false);
  onRowDirtyChange?.(id, {
    hasChanges: false,
    isValid: true,
    errors: [],
    payload: null,
    original: originalData,
  });
};

  // Eliminar categoría (llama a la prop del Manager)
  const handleDeleteClick = (event) => {
    event.stopPropagation();
    onRequestDeleteSingle(id); // Llama a la función pasada por props
  };

  // Si la categoría es inválida, mostrar una fila indicándolo
   if (!categoria || !id) {
    return (
      <TableRow>
        <TableCell colSpan={10} align="center" sx={{ color: 'error.main' }}>
          Error: Datos de categoría inválidos.
        </TableCell>
      </TableRow>
    );
  }


  return (
    <TableRow
      hover
      role="checkbox" // Para accesibilidad con el checkbox
      aria-checked={isSelected}
      tabIndex={-1} // Para navegación por teclado
      selected={isSelected}
      ref={setNodeRef} // Referencia para dnd-kit
      style={style}    // Estilos para dnd-kit (transform, transition, etc.)
      {...attributes}  // Atributos para dnd-kit (rol, etc.) - NO PONER LISTENERS AQUÍ
        sx={{
    '&:hover': {
      backgroundColor: (t) => alpha(stripe(t), 1), // mismo tono, un poco más notorio
      filter: 'saturate(1.05)',
    },
  }}
    >
            {/* Checkbox */}
<TableCell
  padding="checkbox"
  onClick={(e) => {
    if (selectionLocked) {
      // bloquea selección mientras hay edición
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    handleClick(e, id);
  }}
>
    <Tooltip title={selectionLocked ? 'No puedes seleccionar mientras hay edición en curso' : ''}>
      <Checkbox
        color="primary"
        checked={isSelected}
        disabled={selectionLocked}
        inputProps={{ 'aria-labelledby': `categoria-checkbox-${id}` }}
        // Evita que el click del checkbox burbujee a la celda
        onClick={(e) => e.stopPropagation()}
        // Toggle explícito al marcar/desmarcar el checkbox
        onChange={(e) => {
          e.stopPropagation();
          if (selectionLocked) return; // extra defensa
          handleClick(e, id);
        }}
      />
      </Tooltip>
      </TableCell>
            {/* Celda para el Drag Handle */}
<TableCell align="center" sx={{ p: 0, width: 36 }}>
         <Tooltip title="Arrastrar para reordenar">
           {/* IconButton como contenedor del icono y los listeners */}
   <IconButton
    {...listeners}
    size="small"
    aria-label="Mover categoría"
    sx={{
      cursor: 'grab',
      '&:hover': { bgcolor: 'action.hover' },
       touchAction: 'none',
       '&:active': { cursor: 'grabbing' },
    }}
  >
    <DragIndicatorIcon fontSize="small" />
            </IconButton>
         </Tooltip>
      </TableCell>

      {/* #Cat (ya no tiene listeners) */}
      <TableCell sx={{ width: '60px', fontWeight: 'medium' }}>
          {categoria.catNumber ?? '-'}
      </TableCell>

      {/* Código */}
      <TableCell sx={{ minWidth: '100px' }}>
<TextField
  name="codigo"
  value={editFormData.codigo}
  onChange={handleFieldChange}
  variant="outlined"
  size="small"
  fullWidth
  required
  error={!editFormData.codigo.trim() && hasChanges}
  sx={editableCellSx}
/>
      </TableCell>

      {/* Nombre */}
      <TableCell sx={{ minWidth: '150px' }}>
<TextField
  name="nombre"
  value={editFormData.nombre}
  onChange={handleFieldChange}
  variant="outlined"
  size="small"
  fullWidth
  required
  error={!editFormData.nombre.trim() && hasChanges}
  sx={editableCellSx}
/>
      </TableCell>

      {/* Edad Mínima */}
      <TableCell align="center" sx={{ width: '80px' }}>
<TextField
  name="edadMin"
  type="number"
  value={editFormData.edadMin}
  onChange={handleFieldChange}
  variant="outlined"
  size="small"
  required
  inputProps={{ min: 0, style: { textAlign: 'center' } }}
  error={(editFormData.edadMin === '' || isNaN(Number(editFormData.edadMin)) || Number(editFormData.edadMin) > Number(editFormData.edadMax)) && hasChanges}
  sx={editableCellSx}
/>
      </TableCell>

      {/* Edad Máxima */}
      <TableCell align="center" sx={{ width: '80px' }}>
 <TextField
  name="edadMax"
  type="number"
  value={editFormData.edadMax}
  onChange={handleFieldChange}
  variant="outlined"
  size="small"
  required
  inputProps={{ min: 0, style: { textAlign: 'center' } }}
  error={(editFormData.edadMax === '' || isNaN(Number(editFormData.edadMax)) || Number(editFormData.edadMin) > Number(editFormData.edadMax)) && hasChanges}
  sx={editableCellSx}
/>
      </TableCell>

      {/* Rango Fechas Nacimiento */}
      <TableCell sx={{ minWidth: '180px' }}>
        {rangoFechasNacimiento}
      </TableCell>

      {/* Fecha Corte */}
      <TableCell sx={{ minWidth: '140px' }}>
<TextField
  name="fechaCorte"
  type="date"
  value={editFormData.fechaCorte}
  onChange={handleFieldChange}
  variant="outlined"
  size="small"
  required
  InputLabelProps={{ shrink: true }}
  error={!editFormData.fechaCorte && hasChanges}
  sx={editableCellSx}
/>
      </TableCell>

{/* Acciones */}
<TableCell align="center">
  <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
    {hasChanges ? (
      // Hay cambios en esta fila
      bulkMode ? (
        // MODO BULK: mostrar SOLO Cancelar para poder salir del bulk desde esta fila
        <Tooltip title="Cancelar cambios de esta fila">
          <span>
            <IconButton
              color="secondary"
              onClick={handleCancelClick}
              disabled={isSaving}
              size="small"
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        // MODO NORMAL: Guardar + Cancelar
        <>
          <Tooltip title="Guardar cambios">
            <span>
              <IconButton
                color="primary"
                onClick={handleSaveClick}
                disabled={isSaving}
                size="small"
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancelar cambios">
            <span>
              <IconButton
                color="secondary"
                onClick={handleCancelClick}
                disabled={isSaving}
                size="small"
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )
    ) : (
      // Sin cambios en esta fila: permitir eliminar (ocultar en bulk para evitar confusiones)
      !bulkMode && (
        <Tooltip title="Eliminar categoría">
          <span>
            <IconButton
              color="error"
              onClick={handleDeleteClick}
              disabled={isSaving}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )
    )}
  </Box>
</TableCell>
    </TableRow>
  );
};

CategoriasRow.propTypes = {
  id: PropTypes.string.isRequired,
  competenciaId: PropTypes.string.isRequired,
  categoria: PropTypes.shape({
    id: PropTypes.string.isRequired,
    catNumber: PropTypes.number, // Puede ser null
    codigo: PropTypes.string,
    nombre: PropTypes.string,
    edadMin: PropTypes.number,
    edadMax: PropTypes.number,
    fechaCorte: PropTypes.string,
  }), // Hacer opcional si puede ser null/undefined inicialmente? No, Table debería filtrar antes.
  onUpdateCategoria: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  setSnackbar: PropTypes.func.isRequired,
  onRequestDeleteSingle: PropTypes.func.isRequired,
  onRowDirtyChange: PropTypes.func,
  bulkMode: PropTypes.bool,  
  
};

export default CategoriasRow;